import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import Calendar from 'react-calendar';
import { AnyIfEmpty, useDispatch, useSelector } from 'react-redux';
import CalendarIcon from '../../../images/calendar.svg';
import ClockIcon from '../../../images/clock.svg';
import iconDelete from '../../../images/icon-close.svg';
import NoUnits from '../../../images/no-units-found.svg';
import Divider from '../../../images/right-chevrolet.svg';
import { getAppointmentVisitorList } from '../../../redux/appointment-visitors/actions/appointment-visitor.action';
import { AppDispatch, IRootState } from '../../../redux/rootReducer';
import Pagination from '../../common/pagination/pagination';
import TinyLoader from '../../common/tiny-loader/TinyLoader';
import styles from '../Locker.module.css';
import downloadIcon from '../../../images/icon-download.svg';
import LuxerOne from '../../../images/luxerOne.jpeg';

interface LockerListProps {
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    calendarInfo: AnyIfEmpty<object>;
}

interface VisitorList {
    appointmentId: string;
    photo_url: string;
    formatted_datetime: string;
}

const LockerList = ({ setOpen, calendarInfo }: LockerListProps) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [showPagination, setShowPagination] = useState(true);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [startDate, setStartDate] = useState<string>(moment(calendarInfo?.appointmentDate).utc().format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState<string>(moment(calendarInfo?.appointmentDate).utc().format('YYYY-MM-DD'));
    const hours = 'hour';
    const [startTime, setStartTime] = useState<string>(
        moment(calendarInfo?.appointmentStartTime, 'hh:mm A').subtract(15, 'minutes').format('HH:mm:ss'),
    );
    const [endTime, setEndTime] = useState<string>(
        moment(moment(calendarInfo?.appointmentEndTime, 'hh:mm A').add(45, 'minutes')).format('HH:mm:ss'),
    );
    const [selectedDate, setSelectedDate] = useState<Date>();
    const [dataFetched, setDataFetched] = useState(false);
    const [tempData, setTempData] = useState([]);
    const [tempLoading, setTempLoading] = useState(false);

    const dispatch: AppDispatch = useDispatch();

    const userRef = useRef<HTMLInputElement>(null);

    const pageLimit = 5;

    useEffect(() => {
        dispatch(
            getAppointmentVisitorList({
                appointmentId: calendarInfo?.appointmentId,
                currentPage: currentPage,
                pageLimit: pageLimit,
                startRange: startDate !== '' && startTime !== '' ? `${startDate} ${startTime}` : '',
                endRange: endDate !== '' && endTime !== '' ? `${endDate} ${endTime}` : '',
            }),
        );

        setDataFetched(true);

        document.addEventListener('mousedown', (event: any) => {
            if (userRef?.current && !userRef?.current.contains(event.target)) {
                setShowDatePicker(false);
            }
        });
    }, []);

    const { data, loading, count, page } = useSelector((state: IRootState) => state.appointmentVisitor);

    useEffect(() => {
        setTempData(data);
    }, [data]);

    useEffect(() => {
        validateEndTime();
    }, [startDate, endDate]);

    const getAPIData = (newStartDate: string, newEndDate: string, newStartTime: string, newEndTime: string) => {
        setTempLoading(true);
        const filterStartDate = moment(startDate).format('YYYY-MM-DD');
        const filterStartTime = moment(startTime, 'hh:mm').format('HH:mm:ss');

        const filterEndDate = moment(endDate).format('YYYY-MM-DD');
        const filterEndTime = moment(endTime, 'hh:mm').format('HH:mm:ss');

        if (startDate !== '' && endDate !== '' && startTime !== '' && endTime !== '') {
            setTempData([]);
            dispatch(
                getAppointmentVisitorList(
                    {
                        appointmentId: calendarInfo?.appointmentId,
                        startRange: `${newStartDate !== '' ? newStartDate : filterStartDate} ${newStartTime !== '' ? moment(newStartTime, 'HH:mm').format('hh:mm:ss A') : filterStartTime
                            }`,
                        endRange: `${newEndDate !== '' ? newEndDate : filterEndDate} ${newEndTime !== '' ? moment(newEndTime, 'HH:mm').format('hh:mm:ss A') : filterEndTime
                            }`,
                        currentPage: 1,
                        pageLimit: pageLimit,
                    },
                    setShowPagination,
                    setTempLoading,
                ),
            );
            setCurrentPage(page);
            setDataFetched(true);
        }
    };

    const dateSelector = () => {
        return moment(startDate).format('MM-DD-YYYY') === moment(endDate).format('MM-DD-YYYY') ? (
            <span>{moment(startDate).format('MM-DD-YYYY')}</span>
        ) : (
            <>
                <span>{moment(startDate).format('MM-DD-YYYY')}</span>
                <span>{' to '}</span>
                <span>{moment(endDate).format('MM-DD-YYYY')}</span>
            </>
        );
    };

    const validateStartTime = () => {
        if (endTime !== '') {
            if (
                moment(moment(startDate).format('DD-MM-YYYY'), 'DD-MM-YYYY').isSame(
                    moment(moment(endDate).format('DD-MM-YYYY'), 'DD-MM-YYYY'),
                )
            ) {
                if (!moment(startTime, 'HH:mm').isBefore(moment(endTime, 'HH:mm'))) {
                    setStartTime('');
                    setTempLoading(false);
                } else {
                    getAPIData('', '', startTime, '');
                }
            } else {
                getAPIData('', '', startTime, '');
            }
        }
    };

    const validateEndTime = () => {
        if (startTime !== '') {
            if (
                moment(moment(startDate).format('DD-MM-YYYY'), 'DD-MM-YYYY').isSame(
                    moment(moment(endDate).format('DD-MM-YYYY'), 'DD-MM-YYYY'),
                )
            ) {
                if (!moment(startTime, 'HH:mm').isBefore(moment(endTime, 'HH:mm'))) {
                    setEndTime('');
                    setTempLoading(false);
                } else {
                    getAPIData('', '', '', endTime);
                }
            } else {
                getAPIData('', '', '', endTime);
            }
        }
    };

    const onChange = async (date: Date) => {
        setDataFetched(false);
        setSelectedDate(date);
        startTime !== '' && endTime !== '' && setShowPagination(false);
        startTime !== '' && endTime !== '' && setTempLoading(true);
        if (Object.keys(date).length === 2) {
            setShowDatePicker(false);
            setStartDate(moment(Object.values(date)[0]).locale('en').format('YYYY-MM-DD'));
            setEndDate(moment(Object.values(date)[1]).locale('en').format('YYYY-MM-DD'));
            getAPIData(
                moment(Object.values(date)[0]).locale('en').format('YYYY-MM-DD'),
                moment(Object.values(date)[1]).locale('en').format('YYYY-MM-DD'),
                '',
                '',
            );
        }
    };

    const resetFilter = () => {
        setStartDate(moment(calendarInfo?.appointmentDate).utc().format('YYYY-MM-DD'));
        setEndDate(moment(calendarInfo?.appointmentDate).utc().format('YYYY-MM-DD'));
        setStartTime(moment(calendarInfo?.appointmentStartTime, 'hh:mm A').subtract(15, 'minutes').format('HH:mm:ss'));
        setEndTime(moment(moment(calendarInfo?.appointmentEndTime, 'hh:mm A').add(45, 'minutes')).format('HH:mm:ss'));
        setShowDatePicker(false);
        setSelectedDate(undefined);
        setShowPagination(false);
        dispatch(
            getAppointmentVisitorList(
                {
                    appointmentId: calendarInfo?.appointmentId,
                    startRange: `${moment(calendarInfo?.appointmentDate).utc().format('YYYY-MM-DD')} ${moment(
                        calendarInfo?.appointmentStartTime,
                        'hh:mm A',
                    ).format('HH:mm:ss')}`,
                    endRange: `${moment(calendarInfo?.appointmentDate).utc().format('YYYY-MM-DD')} ${moment(
                        moment(calendarInfo?.appointmentEndTime, 'hh:mm A').add(1, hours),
                    ).format('HH:mm:ss')}`,
                    currentPage: 1,
                    pageLimit: pageLimit,
                },
                setShowPagination,
            ),
        );
        setCurrentPage(1);
    };

    const requestNewData = (page: number) => {
        dispatch(
            getAppointmentVisitorList({
                appointmentId: calendarInfo?.appointmentId,
                currentPage: page,
                pageLimit: pageLimit,
                startRange: startDate !== '' && startTime !== '' ? `${startDate} ${startTime}` : '',
                endRange: endDate !== '' && endTime !== '' ? `${endDate} ${endTime}` : '',
            }),
        );
    };

    const removeFocus = (tageName: string) => {
        document.getElementById(tageName)?.blur();

        if (!dataFetched) {
            tageName.toUpperCase() === 'STARTTIME' ? validateStartTime() : validateEndTime();
        }
    };

    return (
        <div className={styles.modalWrapper}>
            <div className={styles.header}>
                <h2 className={styles.modalTitle}>{`Locker ${calendarInfo?.locker_number < 10 ? `0${calendarInfo?.locker_number}` : calendarInfo?.locker_number
                    } History`}</h2>

                <img onClick={() => setOpen(false)} src={iconDelete} alt="Delete" />
            </div>
            <div className={styles.modalBody}>
                <div className={styles.filterSection}>
                    <div className={styles.filterCalendarWrapper}>
                        <p className={styles.pickerTitle}>Select Date</p>
                        <div onClick={() => setShowDatePicker(!showDatePicker)} className={styles.datePickerInput}>
                            <span>{startDate !== '' && endDate !== '' ? dateSelector() : 'Date'}</span>
                            <img src={CalendarIcon} alt="calendar" className={styles.calendarIcon} />
                        </div>
                        {showDatePicker && (
                            <div className={styles.calendarWrapper} ref={userRef}>
                                <Calendar
                                    maxDate={new Date()}
                                    onChange={async (date: Date) => {
                                        onChange(date);
                                    }}
                                    next2Label={null}
                                    selectRange={true}
                                    prev2Label={null}
                                    allowPartialRange={false}
                                    value={selectedDate}
                                />
                            </div>
                        )}
                    </div>

                    <div className={`${styles.filterCalendarWrapper} ${styles.filterTimeWrapper}`}>
                        <p className={styles.pickerTitle}>Select time</p>
                        <div className={styles.datePickerInput}>
                            <div className={styles.timeWrapper}>
                                <input
                                    id="startTime"
                                    type="time"
                                    name="startTime"
                                    value={startTime}
                                    disabled={startDate === '' && endDate === ''}
                                    className={`${styles.dateInput} ${styles.startTime}`}
                                    onChange={(event) => {
                                        setStartTime(moment(event.target.value, 'hh:mm A').format('HH:mm'));
                                        setDataFetched(false);
                                    }}
                                    onBlur={(e) => {
                                        e.preventDefault();
                                    }}
                                    onMouseLeave={() => {
                                        removeFocus('startTime');
                                    }}
                                />
                            </div>
                            <div className={styles.dividerImage}>
                                <img src={Divider} alt="divider" />
                            </div>
                            <div className={styles.timeWrapper}>
                                <input
                                    id="endTime"
                                    type="time"
                                    name="closeTime"
                                    value={endTime}
                                    disabled={startDate === '' && endDate === ''}
                                    className={`${styles.dateInput} ${styles.endTime}`}
                                    onChange={(event) => {
                                        setEndTime(moment(event.target.value, 'hh:mm A').format('HH:mm'));
                                        setDataFetched(false);
                                    }}
                                    onBlur={(e) => {
                                        e.preventDefault();
                                    }}
                                    onMouseLeave={() => {
                                        removeFocus('endTime');
                                    }}
                                />
                            </div>
                            <div className={styles.ClockIcon}>
                                <img src={ClockIcon} alt="calendar" />
                            </div>
                        </div>
                    </div>

                    <button
                        className={styles.resetBtn}
                        onClick={resetFilter}
                        disabled={startDate === '' || endDate === '' || (startTime === '' && endTime === '') || tempLoading}>
                        Reset
                    </button>
                </div>
                <div className={`${styles.wrapper} ${styles.bodyWrapper}`}>
                    <table>
                        <thead>
                            <tr className={styles.tableHead}>
                                <th>Photo</th>
                                <th>{`Open Timestamp`}</th>
                                <th>Download Image</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!!tempData?.length &&
                                !tempLoading &&
                                !loading &&
                                tempData.map((value: VisitorList, index: number) => {
                                    return (
                                        <tr key={index}>
                                            <td>
                                                <img src={value?.photo_url ? value.photo_url : LuxerOne} alt="visitor" className={styles.photoStamp} />
                                            </td>
                                            <td>
                                                {moment(value.formatted_datetime, 'YYYY-MM-DD hh:mm:ss A').format('MM-DD-YYYY hh:mm:ss A')}
                                            </td>
                                            <td>
                                                {' '}
                                                <a href={value.photo_url ? value.photo_url : LuxerOne} download>
                                                    {/* <i className="fa fa-download" /> */}
                                                    <img src={downloadIcon} className={styles.download} alt="download" />
                                                </a>
                                            </td>
                                        </tr>
                                    );
                                })}
                            {!tempLoading && !loading && data?.length === 0 && (
                                <div className={styles.noUnits}>
                                    <img src={NoUnits} alt="no-units" className={styles.noDataImage} />
                                    <p className={styles.noUnitsFoundText}>No Data Found</p>
                                </div>
                            )}
                            {(tempLoading || loading) && (
                                <tr className={styles.loading}>
                                    <td colSpan={2} rowSpan={4}>
                                        <TinyLoader />
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className={styles.paginationContainer}>
                    {showPagination && !!tempData.length && (
                        <Pagination
                            className="pagination-bar"
                            currentPage={currentPage}
                            totalCount={Math.ceil(count / pageLimit)}
                            pageSize={1}
                            onPageChange={(page: number) => {
                                setCurrentPage(page);
                                requestNewData(page);
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default LockerList;
